import React, { useState, useEffect } from 'react';
import { X, Warning, Info, WarningCircle } from '@phosphor-icons/react';
import useApi from '../../utils/AxiosWrapper';
import { safeGetItem, safeSetItem } from '../../shared/safeStorage';

interface SystemAlert {
  alert_id: string;
  alert_type: 'outage' | 'maintenance' | 'warning';
  title: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  affected_services: string[];
  flag_id: string | null;
  target_type: 'all' | 'affected_users';
  active: boolean;
  start_time: string;
  end_time: string | null;
}

interface AlertBannerProps {
  alert: SystemAlert;
  onDismiss: (alertId: string) => void;
}

interface SeverityStyle {
  container: string;
  iconWrap: string;
  pill: string;
  label: string;
}

const SEVERITY_STYLES: Record<SystemAlert['severity'], SeverityStyle> = {
  critical: {
    container: 'border-danger/30 bg-bgred',
    iconWrap: 'bg-danger text-white',
    pill: 'bg-danger text-white',
    label: 'Critical',
  },
  high: {
    container: 'border-danger/25 bg-bgred',
    iconWrap: 'bg-danger/10 text-danger',
    pill: 'bg-danger/10 text-danger',
    label: 'High',
  },
  medium: {
    container: 'border-accent/40 bg-bgyellow',
    iconWrap: 'bg-accent/20 text-[#8A5A00]',
    pill: 'bg-accent/20 text-[#8A5A00]',
    label: 'Medium',
  },
  low: {
    container: 'border-infoPrimary/25 bg-[#EAF4FE]',
    iconWrap: 'bg-infoPrimary/10 text-infoPrimary',
    pill: 'bg-infoPrimary/10 text-infoPrimary',
    label: 'Low',
  },
};

const AlertBanner: React.FC<AlertBannerProps> = ({ alert, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const styles = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.low;

  const handleDismiss = () => {
    setIsExiting(true);
    window.setTimeout(() => onDismiss(alert.alert_id), 200);
  };

  const getIcon = () => {
    switch (alert.alert_type) {
      case 'outage':
        return <WarningCircle size={20} weight="fill" />;
      case 'maintenance':
        return <Info size={20} weight="fill" />;
      case 'warning':
        return <Warning size={20} weight="fill" />;
      default:
        return <Info size={20} weight="fill" />;
    }
  };

  return (
    <div
      role="alert"
      aria-live={alert.severity === 'critical' ? 'assertive' : 'polite'}
      className={`w-full overflow-hidden rounded-xl border font-inter shadow-sm transition duration-200 ease-out ${
        styles.container
      } ${isExiting ? '-translate-y-1 opacity-0' : 'translate-y-0 opacity-100'}`}
    >
      <div className="flex items-start gap-3 px-4 py-3.5 md:px-5">
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${styles.iconWrap}`}
        >
          {getIcon()}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={`inline-flex flex-shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles.pill}`}
            >
              {styles.label}
            </span>
            <h3 className="text-sm font-semibold leading-snug text-primary md:text-[15px]">
              {alert.title}
            </h3>
          </div>

          {alert.message && (
            <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-graysecondary">
              {alert.message}
            </p>
          )}

          {alert.affected_services.length > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-graytertiary">
                Affected services
              </span>
              {alert.affected_services.map((service) => (
                <span
                  key={service}
                  className="inline-flex items-center rounded-md border border-black/5 bg-white/70 px-2 py-0.5 text-xs font-medium text-graysecondary"
                >
                  {service}
                </span>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={handleDismiss}
          aria-label="Dismiss alert"
          className="-mr-1 -mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-graytertiary transition-colors hover:bg-black/5 hover:text-primary"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export const SystemAlertBanner: React.FC = () => {
  const { callProtectedApi } = useApi();
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load previously dismissed alerts from localStorage
    const stored = safeGetItem('dismissedAlerts');
    if (stored) {
      try {
        setDismissedAlerts(JSON.parse(stored));
      } catch {
        // ignore parse errors
      }
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await callProtectedApi(
        '/featureFlags/systemAlerts/active',
        'get'
      );

      if (response?.data?.data?.alerts) {
        setAlerts(response.data.data.alerts);
      }
    } catch (err) {
      console.error('Error fetching system alerts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = (alertId: string) => {
    const dismissed = [...dismissedAlerts, alertId];
    setDismissedAlerts(dismissed);
    safeSetItem('dismissedAlerts', JSON.stringify(dismissed));
  };

  const visibleAlerts = alerts.filter(
    (alert) =>
      !dismissedAlerts.includes(alert.alert_id) &&
      (!alert.end_time || new Date(alert.end_time) > new Date())
  );

  if (isLoading || visibleAlerts.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-3">
      {visibleAlerts.map((alert) => (
        <AlertBanner
          key={alert.alert_id}
          alert={alert}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
};

export default SystemAlertBanner;
